import * as THREE from 'three'

/**
 * Analyze and report boundary edge distribution
 * @param {THREE.BufferGeometry} geometry - The geometry to analyze
 * @returns {Object} - Statistics about boundary edges
 */
export function analyzeBoundaryEdges(geometry) {
  if (!geometry.index) return null

  const indices = geometry.index.array
  const positions = geometry.attributes.position
  const posArray = positions.array

  // Build edge map
  const edgeMap = new Map()
  for (let i = 0; i < indices.length; i += 3) {
    const v0 = indices[i]
    const v1 = indices[i + 1]
    const v2 = indices[i + 2]

    const edges = [[v0, v1], [v1, v2], [v2, v0]]
    for (const [a, b] of edges) {
      const key = a < b ? `${a},${b}` : `${b},${a}`
      if (!edgeMap.has(key)) edgeMap.set(key, [])
      edgeMap.get(key).push({ tri: Math.floor(i / 3), verts: [a, b] })
    }
  }

  // Classify edges
  const boundaryEdges = []
  for (const [edge, tris] of edgeMap.entries()) {
    if (tris.length === 1) {
      const [v0, v1] = edge.split(',').map(Number)
      const p0x = posArray[v0 * 3], p0y = posArray[v0 * 3 + 1], p0z = posArray[v0 * 3 + 2]
      const p1x = posArray[v1 * 3], p1y = posArray[v1 * 3 + 1], p1z = posArray[v1 * 3 + 2]
      const dist = Math.sqrt((p1x - p0x) ** 2 + (p1y - p0y) ** 2 + (p1z - p0z) ** 2)
      boundaryEdges.push({ edge, v0, v1, distance: dist, tri: tris[0].tri })
    }
  }

  return {
    totalBoundaryEdges: boundaryEdges.length,
    edges: boundaryEdges,
  }
}

/**
 * Repair mesh topology by welding boundary edges and removing duplicate faces
 * @param {THREE.BufferGeometry} geometry - The geometry to repair
 * @param {Object} options - Repair options
 * @param {number} options.weldTolerance - Distance tolerance for welding boundary edges (default: 1e-3)
 * @param {boolean} options.removeDuplicates - Remove duplicate faces (default: true)
 * @param {boolean} options.verbose - Detailed logging (default: false)
 * @returns {Object} - Repair results with statistics
 */
export function repairMeshTopology(geometry, options = {}) {
  const {
    weldTolerance = 1e-3,
    removeDuplicates = true,
    verbose = false,
  } = options

  const startTime = performance.now()
  const results = {
    boundaryEdgesBefore: 0,
    boundaryEdgesAfter: 0,
    boundaryEdgesWelded: 0,
    duplicatesRemoved: 0,
    parallelEdgesMerged: 0,
    verticesStitched: 0,
    nonManifoldEdgesFixed: 0,
    timeTaken: 0,
  }

  if (!geometry.index) {
    console.warn('⚠️ [TopologyRepair] Cannot repair non-indexed geometry')
    return results
  }

  // Analyze before
  const analysisBefore = analyzeBoundaryEdges(geometry)
  results.boundaryEdgesBefore = analysisBefore.totalBoundaryEdges

  if (verbose && analysisBefore.totalBoundaryEdges > 0) {
    console.log(`\n📊 BOUNDARY EDGES ANALYSIS (before repair):`)
    console.log(`├─ Total boundary edges: ${analysisBefore.totalBoundaryEdges}`)
    const distances = analysisBefore.edges.map(e => e.distance)
    const avgDist = distances.reduce((a, b) => a + b, 0) / distances.length
    const maxDist = Math.max(...distances)
    const minDist = Math.min(...distances)
    console.log(`├─ Distance range: ${minDist.toExponential(2)} to ${maxDist.toExponential(2)}`)
    console.log(`└─ Average distance: ${avgDist.toExponential(2)}\n`)
  }

  // Step 1: Identify and weld boundary edges
  results.boundaryEdgesWelded = weldBoundaryEdges(geometry, weldTolerance, verbose)

  // Step 2: Remove duplicate faces
  if (removeDuplicates) {
    results.duplicatesRemoved = removeDuplicateFaces(geometry, verbose)
  }

  // Step 3: More aggressive edge welding if still many boundary edges remain
  const analysisAfter = analyzeBoundaryEdges(geometry)
  results.boundaryEdgesAfter = analysisAfter.totalBoundaryEdges

  if (results.boundaryEdgesAfter > results.boundaryEdgesBefore * 0.5 && results.boundaryEdgesAfter > 50) {
    // Still many boundary edges - try aggressive welding with larger tolerance
    const aggressiveTolerance = weldTolerance * 5
    if (verbose) {
      console.log(`⚠️ Still ${results.boundaryEdgesAfter} boundary edges remaining. Trying aggressive weld with tolerance: ${aggressiveTolerance}`)
    }
    const additionalWelded = weldBoundaryEdges(geometry, aggressiveTolerance, verbose)
    results.boundaryEdgesWelded += additionalWelded

    const analysisFinal = analyzeBoundaryEdges(geometry)
    results.boundaryEdgesAfter = analysisFinal.totalBoundaryEdges
  }

  // Step 4: Merge parallel boundary edges (duplicate surface patches)
  // This handles cases where two nearly-identical surfaces exist with separate vertex indices
  const parallelMerged = mergeParallelBoundaryEdges(geometry, weldTolerance, verbose)
  results.parallelEdgesMerged = parallelMerged

  if (parallelMerged > 0) {
    const analysisFinal = analyzeBoundaryEdges(geometry)
    results.boundaryEdgesAfter = analysisFinal.totalBoundaryEdges
  }

  // Step 5: Stitch nearby boundary vertices (close mesh segments)
  // This finds vertices along seams that are close but offset and merges them
  // Effectively closes small gaps and connects mesh segments
  const stitchedVertices = stitchNearbyBoundaryVertices(geometry, weldTolerance, verbose)
  results.verticesStitched = stitchedVertices

  if (stitchedVertices > 0) {
    const analysisFinal = analyzeBoundaryEdges(geometry)
    results.boundaryEdgesAfter = analysisFinal.totalBoundaryEdges
  }

  // Step 6: Fix non-manifold edges (edges shared by 3+ triangles)
  // This detects and removes overlapping faces that create invalid topology
  const nonManifoldFixed = fixNonManifoldEdges(geometry, verbose)
  results.nonManifoldEdgesFixed = nonManifoldFixed

  if (nonManifoldFixed > 0) {
    const analysisFinal = analyzeBoundaryEdges(geometry)
    results.boundaryEdgesAfter = analysisFinal.totalBoundaryEdges
  }

  results.timeTaken = performance.now() - startTime

  // Log results
  console.log(`\n🔧 TOPOLOGY REPAIR:`)
  console.log(`├─ Boundary edges before: ${results.boundaryEdgesBefore}`)
  console.log(`├─ Boundary edges after: ${results.boundaryEdgesAfter}`)
  console.log(`├─ Boundary edges welded: ${results.boundaryEdgesWelded} ${results.boundaryEdgesWelded > 0 ? '✅' : '⚫'}`)
  if (results.parallelEdgesMerged > 0) {
    console.log(`├─ Parallel edges merged: ${results.parallelEdgesMerged} ✅`)
  }
  if (results.verticesStitched > 0) {
    console.log(`├─ Boundary vertices stitched: ${results.verticesStitched} ✅`)
  }
  if (results.nonManifoldEdgesFixed > 0) {
    console.log(`├─ Non-manifold edges fixed: ${results.nonManifoldEdgesFixed} ✅`)
  }
  console.log(`├─ Duplicate faces removed: ${results.duplicatesRemoved} ${results.duplicatesRemoved > 0 ? '✅' : '⚫'}`)
  console.log(`└─ Time taken: ${results.timeTaken.toFixed(2)}ms${results.boundaryEdgesAfter > 100 ? ' ⚠️ Many boundary edges still remain' : ''}\n`)

  return results
}

/**
 * Merge parallel boundary edges (duplicate surface patches)
 * Detects cases where two edges are in the same location but use different vertices
 * This indicates duplicate/overlapping surface patches from floating-point errors
 * @param {THREE.BufferGeometry} geometry - The geometry to repair
 * @param {number} tolerance - Distance tolerance for considering edges parallel
 * @param {boolean} verbose - Detailed logging
 * @returns {number} - Number of parallel edge pairs merged
 */
function mergeParallelBoundaryEdges(geometry, tolerance, verbose = false) {
  if (!geometry.index) return 0

  const indices = geometry.index.array
  const positions = geometry.attributes.position
  const posArray = positions.array

  // Get all boundary edges
  const edgeMap = new Map()
  for (let i = 0; i < indices.length; i += 3) {
    const v0 = indices[i]
    const v1 = indices[i + 1]
    const v2 = indices[i + 2]

    const edges = [[v0, v1], [v1, v2], [v2, v0]]
    for (const [a, b] of edges) {
      const key = a < b ? `${a},${b}` : `${b},${a}`
      if (!edgeMap.has(key)) {
        edgeMap.set(key, [])
      }
      edgeMap.get(key).push([a, b])
    }
  }

  // Find boundary edges (edges with only one triangle)
  const boundaryEdges = []
  const boundaryEdgeMap = new Map() // Maps edge to its vertices

  for (const [edgeKey, triangles] of edgeMap.entries()) {
    if (triangles.length === 1) {
      const [v0, v1] = edgeKey.split(',').map(Number)
      const midX = (posArray[v0 * 3] + posArray[v1 * 3]) / 2
      const midY = (posArray[v0 * 3 + 1] + posArray[v1 * 3 + 1]) / 2
      const midZ = (posArray[v0 * 3 + 2] + posArray[v1 * 3 + 2]) / 2
      const len = Math.sqrt(
        (posArray[v1 * 3] - posArray[v0 * 3]) ** 2 +
        (posArray[v1 * 3 + 1] - posArray[v0 * 3 + 1]) ** 2 +
        (posArray[v1 * 3 + 2] - posArray[v0 * 3 + 2]) ** 2
      )

      boundaryEdges.push({ key: edgeKey, v0, v1, mid: [midX, midY, midZ], len })
      boundaryEdgeMap.set(edgeKey, [v0, v1])
    }
  }

  // Group boundary edges by spatial location (they're parallel if their midpoints and lengths are similar)
  const edgeGroups = new Map() // Maps spatial hash to list of edges

  for (const edge of boundaryEdges) {
    // Create spatial hash of edge center (quantize to tolerance)
    const hash = `${Math.round(edge.mid[0] / tolerance)},${Math.round(edge.mid[1] / tolerance)},${Math.round(edge.mid[2] / tolerance)},${Math.round(edge.len / tolerance)}`

    if (!edgeGroups.has(hash)) {
      edgeGroups.set(hash, [])
    }
    edgeGroups.get(hash).push(edge)
  }

  // For each group of edges in the same location, merge their vertices
  const mergeMap = new Map() // Maps old vertex to new vertex
  let parallelCount = 0

  for (const edgeGroup of edgeGroups.values()) {
    if (edgeGroup.length < 2) continue // Need at least 2 edges to be parallel

    // Sort edges by their first vertex index (for consistent merging)
    edgeGroup.sort((a, b) => a.v0 - b.v0)

    // Use first edge as the reference
    const refEdge = edgeGroup[0]

    if (verbose) {
      console.log(`  └─ Found ${edgeGroup.length} parallel edges at position (${refEdge.mid[0].toFixed(3)}, ${refEdge.mid[1].toFixed(3)}, ${refEdge.mid[2].toFixed(3)})`)
    }

    // Merge all other edges to reference edge
    for (let i = 1; i < edgeGroup.length; i++) {
      const edge = edgeGroup[i]

      // Find best match orientation for this edge
      const p0 = [posArray[refEdge.v0 * 3], posArray[refEdge.v0 * 3 + 1], posArray[refEdge.v0 * 3 + 2]]
      const p1 = [posArray[refEdge.v1 * 3], posArray[refEdge.v1 * 3 + 1], posArray[refEdge.v1 * 3 + 2]]
      const q0 = [posArray[edge.v0 * 3], posArray[edge.v0 * 3 + 1], posArray[edge.v0 * 3 + 2]]
      const q1 = [posArray[edge.v1 * 3], posArray[edge.v1 * 3 + 1], posArray[edge.v1 * 3 + 2]]

      const dist00 = Math.hypot(p0[0] - q0[0], p0[1] - q0[1], p0[2] - q0[2])
      const dist01 = Math.hypot(p0[0] - q1[0], p0[1] - q1[1], p0[2] - q1[2])
      const dist11 = Math.hypot(p1[0] - q1[0], p1[1] - q1[1], p1[2] - q1[2])
      const dist10 = Math.hypot(p1[0] - q0[0], p1[1] - q0[1], p1[2] - q0[2])

      // Best match: minimum sum of distances
      if (dist00 + dist11 < dist01 + dist10) {
        // Same direction: q0→q1 matches p0→p1
        mergeMap.set(edge.v0, refEdge.v0)
        mergeMap.set(edge.v1, refEdge.v1)
      } else {
        // Opposite direction: q0→q1 matches p1→p0
        mergeMap.set(edge.v0, refEdge.v1)
        mergeMap.set(edge.v1, refEdge.v0)
      }

      parallelCount++
    }
  }

  // Apply vertex merges to index buffer
  if (mergeMap.size > 0) {
    for (let i = 0; i < indices.length; i++) {
      if (mergeMap.has(indices[i])) {
        indices[i] = mergeMap.get(indices[i])
      }
    }
    geometry.index.needsUpdate = true
    geometry.computeVertexNormals()
  }

  return parallelCount
}

/**
 * Fix non-manifold edges by removing overlapping/duplicate triangles
 * Non-manifold edges are edges shared by 3+ triangles, which is topologically invalid
 * @param {THREE.BufferGeometry} geometry - The geometry to repair
 * @param {boolean} verbose - Detailed logging
 * @returns {number} - Number of non-manifold edges fixed
 */
function fixNonManifoldEdges(geometry, verbose = false) {
  if (!geometry.index) return 0

  const indices = geometry.index.array
  const positions = geometry.attributes.position
  const posArray = positions.array

  // Build edge map to find non-manifold edges
  const edgeMap = new Map()
  for (let i = 0; i < indices.length; i += 3) {
    const v0 = indices[i]
    const v1 = indices[i + 1]
    const v2 = indices[i + 2]
    const triIndex = Math.floor(i / 3)

    const edges = [
      [v0, v1, triIndex],
      [v1, v2, triIndex],
      [v2, v0, triIndex],
    ]

    for (const [a, b, tri] of edges) {
      const key = a < b ? `${a},${b}` : `${b},${a}`
      if (!edgeMap.has(key)) {
        edgeMap.set(key, [])
      }
      edgeMap.get(key).push(tri)
    }
  }

  // Find non-manifold edges (edges with 3+ triangles)
  const nonManifoldEdges = []
  for (const [edgeKey, triangles] of edgeMap.entries()) {
    if (triangles.length > 2) {
      nonManifoldEdges.push({ edge: edgeKey, triangles })
    }
  }

  if (verbose && nonManifoldEdges.length > 0) {
    console.log(`  └─ Found ${nonManifoldEdges.length} non-manifold edges`)
  }

  if (nonManifoldEdges.length === 0) return 0

  // For each non-manifold edge, decide which triangles to keep
  const trianglesToRemove = new Set()
  let fixedCount = 0

  for (const { edge, triangles } of nonManifoldEdges) {
    const [v0, v1] = edge.split(',').map(Number)

    // Get triangle data for analysis
    const triData = triangles.map(triIdx => {
      const i0 = triIdx * 3
      const va = indices[i0]
      const vb = indices[i0 + 1]
      const vc = indices[i0 + 2]

      // Calculate triangle normal
      const p0 = new THREE.Vector3(posArray[va * 3], posArray[va * 3 + 1], posArray[va * 3 + 2])
      const p1 = new THREE.Vector3(posArray[vb * 3], posArray[vb * 3 + 1], posArray[vb * 3 + 2])
      const p2 = new THREE.Vector3(posArray[vc * 3], posArray[vc * 3 + 1], posArray[vc * 3 + 2])

      const e1 = new THREE.Vector3().subVectors(p1, p0)
      const e2 = new THREE.Vector3().subVectors(p2, p0)
      const normal = new THREE.Vector3().crossVectors(e1, e2)
      const area = normal.length() / 2
      normal.normalize()

      // Calculate triangle center
      const center = new THREE.Vector3().add(p0).add(p1).add(p2).divideScalar(3)

      return { triIdx, vertices: [va, vb, vc], normal, area, center }
    })

    // Group triangles by similarity (same normal direction and overlapping position)
    const groups = []
    const used = new Set()

    for (let i = 0; i < triData.length; i++) {
      if (used.has(i)) continue

      const group = [i]
      used.add(i)

      for (let j = i + 1; j < triData.length; j++) {
        if (used.has(j)) continue

        // Check if triangles are similar (likely duplicates)
        const normalDot = triData[i].normal.dot(triData[j].normal)
        const centerDist = triData[i].center.distanceTo(triData[j].center)
        const areaDiff = Math.abs(triData[i].area - triData[j].area) / Math.max(triData[i].area, triData[j].area)

        // Same orientation, overlapping position, similar size = duplicates
        if (normalDot > 0.99 && centerDist < 0.01 && areaDiff < 0.1) {
          group.push(j)
          used.add(j)
        }
      }

      groups.push(group)
    }

    // For each group, keep only the first triangle, remove the rest
    let removedInThisEdge = 0
    for (const group of groups) {
      // Keep first, remove rest
      for (let i = 1; i < group.length; i++) {
        const triIdx = triData[group[i]].triIdx
        trianglesToRemove.add(triIdx)
        removedInThisEdge++
      }
    }

    // If we still have more than 2 triangles remaining after removing duplicates,
    // keep the 2 largest by area (most likely the valid ones)
    const remaining = triData.filter((_, idx) => !trianglesToRemove.has(triData[idx].triIdx))
    if (remaining.length > 2) {
      remaining.sort((a, b) => b.area - a.area)
      for (let i = 2; i < remaining.length; i++) {
        trianglesToRemove.add(remaining[i].triIdx)
        removedInThisEdge++
      }
    }

    if (removedInThisEdge > 0) {
      fixedCount++
      if (verbose && fixedCount <= 10) {
        console.log(`     └─ Fixed non-manifold edge ${edge}: removed ${removedInThisEdge} overlapping triangles`)
      }
    }
  }

  // Remove marked triangles from index buffer
  if (trianglesToRemove.size > 0) {
    const newIndices = []
    for (let i = 0; i < indices.length; i += 3) {
      const triIdx = Math.floor(i / 3)
      if (!trianglesToRemove.has(triIdx)) {
        newIndices.push(indices[i], indices[i + 1], indices[i + 2])
      }
    }

    const newIndexArray = new (indices.constructor)(newIndices.length)
    newIndexArray.set(newIndices)
    geometry.setIndex(new THREE.BufferAttribute(newIndexArray, 1))
    geometry.computeVertexNormals()

    if (verbose) {
      console.log(`  └─ Fixed ${fixedCount} non-manifold edges by removing ${trianglesToRemove.size} triangles`)
    }
  }

  return fixedCount
}

/**
 * Stitch nearby boundary vertices to close gaps between mesh segments
 * Finds vertices on boundary edges that are spatially close and merges them
 * This closes seams where mesh segments are offset but nearby
 * @param {THREE.BufferGeometry} geometry - The geometry to repair
 * @param {number} tolerance - Distance tolerance for stitching vertices
 * @param {boolean} verbose - Detailed logging
 * @returns {number} - Number of vertex pairs stitched
 */
function stitchNearbyBoundaryVertices(geometry, tolerance, verbose = false) {
  if (!geometry.index) return 0

  const indices = geometry.index.array
  const positions = geometry.attributes.position
  const posArray = positions.array

  // Step 1: Identify all boundary vertices
  const edgeMap = new Map()
  for (let i = 0; i < indices.length; i += 3) {
    const v0 = indices[i]
    const v1 = indices[i + 1]
    const v2 = indices[i + 2]

    const edges = [[v0, v1], [v1, v2], [v2, v0]]
    for (const [a, b] of edges) {
      const key = a < b ? `${a},${b}` : `${b},${a}`
      if (!edgeMap.has(key)) {
        edgeMap.set(key, [])
      }
      edgeMap.get(key).push([a, b])
    }
  }

  // Collect boundary vertices (vertices on edges that belong to only one triangle)
  const boundaryVertexSet = new Set()
  for (const [edgeKey, triangles] of edgeMap.entries()) {
    if (triangles.length === 1) {
      const [v0, v1] = edgeKey.split(',').map(Number)
      boundaryVertexSet.add(v0)
      boundaryVertexSet.add(v1)
    }
  }

  const boundaryVertices = Array.from(boundaryVertexSet)

  if (verbose && boundaryVertices.length > 0) {
    console.log(`  └─ Found ${boundaryVertices.length} boundary vertices to stitch`)
  }

  // Step 2: Build spatial grid of boundary vertices for proximity search
  const cellSize = tolerance * 2
  const spatialGrid = new Map()

  for (const vIdx of boundaryVertices) {
    const x = posArray[vIdx * 3]
    const y = posArray[vIdx * 3 + 1]
    const z = posArray[vIdx * 3 + 2]

    const cellX = Math.floor(x / cellSize)
    const cellY = Math.floor(y / cellSize)
    const cellZ = Math.floor(z / cellSize)
    const cellKey = `${cellX},${cellY},${cellZ}`

    if (!spatialGrid.has(cellKey)) {
      spatialGrid.set(cellKey, [])
    }
    spatialGrid.get(cellKey).push(vIdx)
  }

  // Step 3: Find nearby vertex pairs and merge them
  const mergeMap = new Map() // Maps old vertex index to new vertex index
  const processed = new Set()
  let stitchedCount = 0

  for (const vIdx of boundaryVertices) {
    if (processed.has(vIdx) || mergeMap.has(vIdx)) continue

    const x = posArray[vIdx * 3]
    const y = posArray[vIdx * 3 + 1]
    const z = posArray[vIdx * 3 + 2]

    // Search in this cell and neighboring cells
    const cellX = Math.floor(x / cellSize)
    const cellY = Math.floor(y / cellSize)
    const cellZ = Math.floor(z / cellSize)

    const nearbyVertices = []
    for (let dx = -1; dx <= 1; dx++) {
      for (let dy = -1; dy <= 1; dy++) {
        for (let dz = -1; dz <= 1; dz++) {
          const cellKey = `${cellX + dx},${cellY + dy},${cellZ + dz}`
          if (spatialGrid.has(cellKey)) {
            nearbyVertices.push(...spatialGrid.get(cellKey))
          }
        }
      }
    }

    // Find closest nearby vertex
    let closestVertex = null
    let closestDist = tolerance

    for (const otherIdx of nearbyVertices) {
      if (otherIdx === vIdx) continue
      if (processed.has(otherIdx) || mergeMap.has(otherIdx)) continue

      const ox = posArray[otherIdx * 3]
      const oy = posArray[otherIdx * 3 + 1]
      const oz = posArray[otherIdx * 3 + 2]

      const dist = Math.sqrt((x - ox) ** 2 + (y - oy) ** 2 + (z - oz) ** 2)

      if (dist < closestDist) {
        closestDist = dist
        closestVertex = otherIdx
      }
    }

    // Merge with closest vertex
    if (closestVertex !== null) {
      // Use the vertex with lower index as the target (consistent merging)
      const target = Math.min(vIdx, closestVertex)
      const source = Math.max(vIdx, closestVertex)

      mergeMap.set(source, target)
      processed.add(source)
      processed.add(target)
      stitchedCount++

      if (verbose && stitchedCount <= 10) {
        console.log(`     └─ Stitching vertex ${source} → ${target} (distance: ${closestDist.toExponential(2)})`)
      }
    }

    processed.add(vIdx)
  }

  // Step 4: Apply vertex merges to index buffer
  if (mergeMap.size > 0) {
    for (let i = 0; i < indices.length; i++) {
      if (mergeMap.has(indices[i])) {
        indices[i] = mergeMap.get(indices[i])
      }
    }
    geometry.index.needsUpdate = true
    geometry.computeVertexNormals()

    if (verbose) {
      console.log(`  └─ Stitched ${stitchedCount} vertex pairs (${mergeMap.size} vertices merged)`)
    }
  }

  return stitchedCount
}

/**
 * Find and merge boundary edges that are geometrically coincident
 * Boundary edges are edges that belong to only one triangle (not shared with another)
 * @param {THREE.BufferGeometry} geometry - The geometry to repair
 * @param {number} tolerance - Distance tolerance for welding
 * @param {boolean} verbose - Detailed logging
 * @returns {number} - Number of boundary edges welded
 */
function weldBoundaryEdges(geometry, tolerance, verbose = false) {
  if (!geometry.index) return 0

  const indices = geometry.index.array
  const positions = geometry.attributes.position
  const posArray = positions.array

  // Build edge map to identify boundary edges
  const edgeMap = new Map()
  for (let i = 0; i < indices.length; i += 3) {
    const v0 = indices[i]
    const v1 = indices[i + 1]
    const v2 = indices[i + 2]

    const edges = [
      [v0, v1],
      [v1, v2],
      [v2, v0],
    ]

    for (const [a, b] of edges) {
      const key = a < b ? `${a},${b}` : `${b},${a}`
      if (!edgeMap.has(key)) {
        edgeMap.set(key, [])
      }
      edgeMap.get(key).push([a, b])
    }
  }

  // Find boundary edges (edges with only one triangle)
  const boundaryEdges = []
  for (const [edge, triangles] of edgeMap.entries()) {
    if (triangles.length === 1) {
      boundaryEdges.push(triangles[0])
    }
  }

  if (verbose && boundaryEdges.length > 0) {
    console.log(`  └─ Welding ${boundaryEdges.length} boundary edges with tolerance ${tolerance.toExponential(2)}`)
  }

  // Build spatial index for efficient proximity search
  const spatialGrid = buildSpatialGrid(positions, tolerance * 2)

  // Track vertices to merge
  const mergeMap = new Map() // Maps old vertex index to new vertex index
  let weldedCount = 0

  // For each boundary edge, try to find a matching boundary edge
  const remaining = new Set(Array.from({length: boundaryEdges.length}, (_, i) => i))

  for (const i of remaining) {
    const [v0, v1] = boundaryEdges[i]

    // Skip if vertices already merged
    if (mergeMap.has(v0) || mergeMap.has(v1)) {
      remaining.delete(i)
      continue
    }

    // Get positions
    const p0 = new THREE.Vector3(posArray[v0 * 3], posArray[v0 * 3 + 1], posArray[v0 * 3 + 2])
    const p1 = new THREE.Vector3(posArray[v1 * 3], posArray[v1 * 3 + 1], posArray[v1 * 3 + 2])

    // Look for a matching boundary edge with opposite direction
    for (const j of remaining) {
      if (i === j) continue

      const [v2, v3] = boundaryEdges[j]

      // Skip if vertices already merged
      if (mergeMap.has(v2) || mergeMap.has(v3)) continue

      const p2 = new THREE.Vector3(posArray[v2 * 3], posArray[v2 * 3 + 1], posArray[v2 * 3 + 2])
      const p3 = new THREE.Vector3(posArray[v3 * 3], posArray[v3 * 3 + 1], posArray[v3 * 3 + 2])

      // Check if edges match: (v0,v1) ↔ (v3,v2) - reversed direction
      const d01 = p0.distanceTo(p3)
      const d10 = p1.distanceTo(p2)
      const distSum = d01 + d10

      if (distSum < tolerance) {
        // Weld these edges: merge v1→v2 and v0→v3
        mergeMap.set(v0, v3)
        mergeMap.set(v1, v2)
        weldedCount++
        remaining.delete(j)
        remaining.delete(i)
        break
      }
    }
  }

  // Apply merges to the index buffer
  if (mergeMap.size > 0) {
    for (let i = 0; i < indices.length; i++) {
      if (mergeMap.has(indices[i])) {
        indices[i] = mergeMap.get(indices[i])
      }
    }
    geometry.index.needsUpdate = true
    geometry.computeVertexNormals()
  }

  return weldedCount
}

/**
 * Remove duplicate faces (triangles that share the same three vertices)
 * Also handles near-duplicate faces where vertices are geometrically very close
 * @param {THREE.BufferGeometry} geometry - The geometry to clean
 * @param {boolean} verbose - Detailed logging
 * @param {number} tolerance - Tolerance for near-duplicates (default: 1e-5)
 * @returns {number} - Number of duplicate faces removed
 */
function removeDuplicateFaces(geometry, verbose = false, tolerance = 1e-5) {
  if (!geometry.index) return 0

  const indices = geometry.index.array
  const positions = geometry.attributes.position
  const posArray = positions.array
  const faceSet = new Set()
  const indicesToRemove = new Set()
  let removedCount = 0
  let nearDupsCount = 0

  // For each triangle, create a canonical representation
  for (let i = 0; i < indices.length; i += 3) {
    if (indicesToRemove.has(i) || indicesToRemove.has(i + 1) || indicesToRemove.has(i + 2)) {
      continue // Already marked for removal
    }

    const v0 = indices[i]
    const v1 = indices[i + 1]
    const v2 = indices[i + 2]

    // Create canonical face key (sorted vertices)
    const verts = [v0, v1, v2].sort((a, b) => a - b)
    const faceKey = `${verts[0]},${verts[1]},${verts[2]}`

    if (faceSet.has(faceKey)) {
      // This is an exact duplicate face
      indicesToRemove.add(i)
      indicesToRemove.add(i + 1)
      indicesToRemove.add(i + 2)
      removedCount++
    } else {
      faceSet.add(faceKey)
    }
  }

  // Second pass: detect near-duplicate faces (same geometric position but different vertex indices)
  if (tolerance > 0) {
    const faceGeometries = new Map() // Maps face center position to face indices

    for (let i = 0; i < indices.length; i += 3) {
      if (indicesToRemove.has(i) || indicesToRemove.has(i + 1) || indicesToRemove.has(i + 2)) {
        continue
      }

      const v0 = indices[i]
      const v1 = indices[i + 1]
      const v2 = indices[i + 2]

      // Calculate face center
      const cx = (posArray[v0 * 3] + posArray[v1 * 3] + posArray[v2 * 3]) / 3
      const cy = (posArray[v0 * 3 + 1] + posArray[v1 * 3 + 1] + posArray[v2 * 3 + 1]) / 3
      const cz = (posArray[v0 * 3 + 2] + posArray[v1 * 3 + 2] + posArray[v2 * 3 + 2]) / 3

      const centerKey = `${Math.round(cx / tolerance)},${Math.round(cy / tolerance)},${Math.round(cz / tolerance)}`

      if (!faceGeometries.has(centerKey)) {
        faceGeometries.set(centerKey, [])
      }
      faceGeometries.get(centerKey).push(i)
    }

    // Check faces with same center for near-duplicates
    for (const faceIndices of faceGeometries.values()) {
      if (faceIndices.length < 2) continue

      for (let i = 0; i < faceIndices.length; i++) {
        const idx1 = faceIndices[i]
        if (indicesToRemove.has(idx1) || indicesToRemove.has(idx1 + 1) || indicesToRemove.has(idx1 + 2)) {
          continue
        }

        for (let j = i + 1; j < faceIndices.length; j++) {
          const idx2 = faceIndices[j]
          if (indicesToRemove.has(idx2) || indicesToRemove.has(idx2 + 1) || indicesToRemove.has(idx2 + 2)) {
            continue
          }

          // Check if faces are geometrically equivalent
          const v0_1 = indices[idx1], v1_1 = indices[idx1 + 1], v2_1 = indices[idx1 + 2]
          const v0_2 = indices[idx2], v1_2 = indices[idx2 + 1], v2_2 = indices[idx2 + 2]

          if (areFacesNearDuplicate(posArray, v0_1, v1_1, v2_1, v0_2, v1_2, v2_2, tolerance * 10)) {
            // Mark second face for removal
            indicesToRemove.add(idx2)
            indicesToRemove.add(idx2 + 1)
            indicesToRemove.add(idx2 + 2)
            nearDupsCount++
          }
        }
      }
    }
  }

  // Rebuild index buffer without duplicates
  if (removedCount > 0 || nearDupsCount > 0) {
    const newIndices = new (indices.constructor)(indices.length - indicesToRemove.size)
    let writeIdx = 0

    for (let i = 0; i < indices.length; i++) {
      if (!indicesToRemove.has(i)) {
        newIndices[writeIdx++] = indices[i]
      }
    }

    geometry.setIndex(new THREE.BufferAttribute(newIndices, 1))
    geometry.computeVertexNormals()

    if (verbose && nearDupsCount > 0) {
      console.log(`  └─ Removed ${removedCount} exact duplicates + ${nearDupsCount} near-duplicates`)
    }
  }

  return removedCount + nearDupsCount
}

/**
 * Check if two faces are geometrically equivalent (near-duplicates)
 * @param {Float32Array} posArray - Position array
 * @param {number} v0_1, v1_1, v2_1 - Vertices of first face
 * @param {number} v0_2, v1_2, v2_2 - Vertices of second face
 * @param {number} tolerance - Distance tolerance
 * @returns {boolean} - Whether faces are near-duplicates
 */
function areFacesNearDuplicate(posArray, v0_1, v1_1, v2_1, v0_2, v1_2, v2_2, tolerance) {
  const getVert = (idx) => [posArray[idx * 3], posArray[idx * 3 + 1], posArray[idx * 3 + 2]]
  const dist = (a, b) => Math.sqrt((a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2 + (a[2] - b[2]) ** 2)

  const p0_1 = getVert(v0_1), p1_1 = getVert(v1_1), p2_1 = getVert(v2_1)
  const p0_2 = getVert(v0_2), p1_2 = getVert(v1_2), p2_2 = getVert(v2_2)

  // Try all permutations of second face to see if it matches first face
  const perms = [
    [[p0_2, p1_2, p2_2]], // Same winding
    [[p0_2, p2_2, p1_2]],
    [[p1_2, p0_2, p2_2]],
    [[p1_2, p2_2, p0_2]],
    [[p2_2, p0_2, p1_2]],
    [[p2_2, p1_2, p0_2]],
    [[p0_2, p1_2, p2_2]], // Opposite winding
    [[p2_2, p1_2, p0_2]],
    [[p1_2, p0_2, p2_2]],
    [[p0_2, p2_2, p1_2]],
    [[p2_2, p0_2, p1_2]],
    [[p1_2, p2_2, p0_2]],
  ]

  for (const perm of perms) {
    const [pa, pb, pc] = perm[0]
    const d1 = dist(p0_1, pa) + dist(p1_1, pb) + dist(p2_1, pc)
    if (d1 < tolerance) {
      return true
    }
  }
  return false
}

/**
 * Build a spatial grid for efficient proximity search
 * @param {THREE.BufferAttribute} positions - Position attribute
 * @param {number} cellSize - Size of each grid cell
 * @returns {Map} - Grid mapping cell coordinates to vertex indices
 */
function buildSpatialGrid(positions, cellSize) {
  const grid = new Map()
  const posArray = positions.array

  for (let i = 0; i < positions.count; i++) {
    const x = posArray[i * 3]
    const y = posArray[i * 3 + 1]
    const z = posArray[i * 3 + 2]

    const coords = getCellCoords(new THREE.Vector3(x, y, z), cellSize)
    const key = `${coords.x},${coords.y},${coords.z}`

    if (!grid.has(key)) {
      grid.set(key, [])
    }
    grid.get(key).push(i)
  }

  return grid
}

/**
 * Get grid cell coordinates for a position
 * @param {THREE.Vector3} position - The position
 * @param {number} cellSize - Size of each grid cell
 * @returns {Object} - Cell coordinates {x, y, z}
 */
function getCellCoords(position, cellSize) {
  return {
    x: Math.floor(position.x / cellSize),
    y: Math.floor(position.y / cellSize),
    z: Math.floor(position.z / cellSize),
  }
}

/**
 * Get vertices in nearby grid cells
 * @param {Map} grid - Spatial grid
 * @param {Object} coords - Center cell coordinates
 * @returns {Array} - Vertex indices in nearby cells
 */
function getNearbyVertices(grid, coords) {
  const nearby = []
  for (let dx = -1; dx <= 1; dx++) {
    for (let dy = -1; dy <= 1; dy++) {
      for (let dz = -1; dz <= 1; dz++) {
        const key = `${coords.x + dx},${coords.y + dy},${coords.z + dz}`
        if (grid.has(key)) {
          nearby.push(...grid.get(key))
        }
      }
    }
  }
  return nearby
}
